import type { ReactNode } from 'react';
import { memo, useCallback, useEffect, useRef, useState } from 'react';

import classNames from 'classnames';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';

import pinkBox from '../../../../../assets/challenge/pinkGift.png';
import purpleBox from '../../../../../assets/challenge/purpleGift.png';
import crownPillowImg from '../../../../../assets/crown-dialog/crown-pillow.webp';
import { analytics } from '../../../../../services/analytics';
import type { UserDailyMission } from '../../../../../stores/daily-missions/types';
import { MissionsWithIntegerProgress } from '../../../../../stores/daily-missions/types';
import type { BundleItem } from '../../../../../stores/rewards/types';
import { useStores } from '../../../../../stores/useStores';
import { formatValueWithK } from '../../../../../utils/helpers';
import { GAME_CARD_ID_PREFIX } from '../../../../GamesList/constants';
import { getProgressBarTitle, getStaticMessage } from '../../../utils';
import { GiftTooltip } from '../../GiftTooltip';
import { useProgressBar } from '../../ProgressBar/useProgressBar';

import styles from './DailyMissionProgressBarContent.module.scss';

interface DailyMissionProgressBarContentProps {
  currentDailyMission: UserDailyMission;
  timer?: ReactNode;
  className?: string;
  disabled?: boolean;
  borderStyle?: string;
}

// eslint-disable-next-line react/display-name
const Tooltip = memo(
  ({ bundleItems, gcCoin, scCoin }: { bundleItems: BundleItem[]; gcCoin: number; scCoin: number }) => {
    return <GiftTooltip bundleItems={bundleItems} gcCoin={gcCoin} scCoin={scCoin} />;
  },
);

function DailyMissionProgressBarContentComponent(props: DailyMissionProgressBarContentProps) {
  const { currentDailyMission, timer, className, disabled, borderStyle } = props;
  const { t } = useTranslation();
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);

  const { dailyMissionsStore, coinTypeStore } = useStores();

  const onAnimationEnd = useCallback(() => {
    if (currentDailyMission.status === 'COMPLETED') {
      dailyMissionsStore.setDialogVisible(true);
      dailyMissionsStore.setEnableGiftAnimation(false);
    }
  }, [currentDailyMission, dailyMissionsStore]);

  const onClick = useCallback(() => {
    dailyMissionsStore.sendAnalyticsEvent({
      event_action: 'impression',
      triggered_by: 'click',
      click_location: 'progress_bar',
    });
    void analytics.sendEvent('daily_mission', {
      event_category: 'lobby_page',
      event_action: 'click',
    });
    dailyMissionsStore.setDialogVisible(true);
  }, [dailyMissionsStore]);

  const onGameClick = useCallback(() => {
    if (!currentDailyMission) {
      return;
    }
    const action_result = currentDailyMission.gameId ? 'scroll_to_game' : undefined;
    dailyMissionsStore.sendAnalyticsEvent({
      event_action: 'click',
      click_location: 'game',
      action_result,
    });
  }, [currentDailyMission, dailyMissionsStore]);

  const onGiftClick = useCallback(() => {
    dailyMissionsStore.sendAnalyticsEvent({
      event_action: 'click',
      click_location: 'gift',
      action_result: 'tooltip_shown',
    });
  }, [dailyMissionsStore]);

  const {
    progress: current,
    goal,
    packageData: { scCoin, gcCoin, bundleItems },
  } = currentDailyMission;

  const coinType = coinTypeStore.coinType;
  const disableAnimation =
    dailyMissionsStore.currentDailyMission?.status === 'COMPLETED' && !dailyMissionsStore.enableGiftAnimation;
  const gameId = currentDailyMission.gameId;
  const gameImageUrl = currentDailyMission.gameImageUrl;
  const integerProgress = MissionsWithIntegerProgress.includes(currentDailyMission.missionType);
  const max = goal;
  const missionId = currentDailyMission.id;
  const missionType = currentDailyMission.missionType;
  const staticMessage = getStaticMessage(currentDailyMission, t);

  const barText = getProgressBarTitle(missionType, coinType, t);

  useEffect(() => {
    let timeout: NodeJS.Timeout | number = 0;
    if (isTooltipVisible) {
      timeout = setTimeout(() => {
        setIsTooltipVisible(false);
      }, 3000);
    }
    return () => clearTimeout(timeout);
  }, [isTooltipVisible]);

  const giftClickHandler = useCallback(() => {
    onGiftClick();
    setIsTooltipVisible(!isTooltipVisible);
  }, [isTooltipVisible, onGiftClick]);

  const animationEndTimeout = useRef<NodeJS.Timeout | number>(0);

  const onGameCircleClick = () => {
    onGameClick?.();
    if (gameId && animationEndTimeout.current === 0) {
      const gameCard = document.querySelector(`#${GAME_CARD_ID_PREFIX}${gameId}`);
      if (gameCard) {
        gameCard.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
        gameCard.classList.add('game-card-highlight');
        clearTimeout(animationEndTimeout.current);
        animationEndTimeout.current = setTimeout(() => {
          gameCard.classList.remove('game-card-highlight');
          clearTimeout(animationEndTimeout.current);
          animationEndTimeout.current = 0;
        }, 4000);
      }
    }
  };

  const { progress, progressDisplay } = useProgressBar({
    current,
    disableAnimation,
    integerProgress,
    max,
    missionId,
    onAnimationEnd,
  });

  return (
    <div className={classNames(styles.dailyMissionBar, className)}>
      <div className={styles.wrapper}>
        {timer && <div className={styles.timerContainer}>{timer}</div>}
        <div className={styles.progressParent} style={borderStyle ? { border: borderStyle } : {}} onClick={onClick}>
          <>
            <div
              className={classNames(
                styles.dailyMissionBarProgress,
                coinType === 'GC' ? styles.gcColor : styles.scColor,
              )}
              style={{ width: `${(progress / max) * 100}%` }}
            />
            <div className={styles.text}>
              {staticMessage ? (
                staticMessage
              ) : (
                <>
                  {progressDisplay}/{formatValueWithK(max, t)}
                  <span data-testid="dailyMission-progress-bar" style={{ marginLeft: '4px' }}>
                    {barText}
                  </span>
                </>
              )}
            </div>
          </>
        </div>
        {!disabled && (
          <div className={styles.giftBox} onClick={giftClickHandler}>
            <>
              {coinType === 'SC' ? <img alt="GiftBox" src={pinkBox} /> : <img alt="GiftBox" src={purpleBox} />}
              {isTooltipVisible && (
                <div className={styles.giftboxTooltip}>
                  <Tooltip bundleItems={bundleItems} gcCoin={gcCoin} scCoin={scCoin} />
                </div>
              )}
            </>
          </div>
        )}
        {!disabled && (
          <div className={styles.gameImage}>
            <div className={styles.circle}></div>
            {gameImageUrl ? (
              <div className={styles.dynamic} onClick={onGameCircleClick}>
                <img alt="Game" src={gameImageUrl} />
              </div>
            ) : (
              <img alt="crown" loading="lazy" src={crownPillowImg} />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export const DailyMissionProgressBarContent = observer(DailyMissionProgressBarContentComponent);
